"use client";

import React, { createContext, useContext, useState, ReactNode } from "react";

// Context types
interface SidebarContextType {
    expanded: boolean;
    setExpanded: (expanded: boolean) => void;
    toggleExpanded: () => void;
    isMobileOpen: boolean;
    setIsMobileOpen: (isMobileOpen: boolean) => void;
}

// Context
const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

// SidebarProvider
interface SidebarProviderProps {
    children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
    // Always start collapsed on reload; user can expand and it stays until they collapse or reload
    const [expanded, setExpandedState] = useState(false);

    const [isMobileOpen, setIsMobileOpen] = useState(false);

    const setExpanded = (value: boolean) => {
        setExpandedState(value);
    };

    const toggleExpanded = () => {
        setExpandedState((prev) => !prev);
    };

    return (
        <SidebarContext.Provider
            value={{
                expanded,
                setExpanded,
                toggleExpanded,
                isMobileOpen,
                setIsMobileOpen,
            }}
        >
            {children}
        </SidebarContext.Provider>
    );
};

// Custom hook
export const useSidebar = (): SidebarContextType => {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error("useSidebar must be used within a SidebarProvider");
    }
    return context;
};
